import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, TrendingUp, Building2, Zap, Heart, Factory, ShoppingCart, Cpu, Pill, Landmark, Fuel, Wifi, ChevronRight } from 'lucide-react';
import SEOHead, { getBreadcrumbSchema } from '../components/SEOHead';

// S&P 500 stocks organized by sector
const SP500_BY_SECTOR = {
  "Information Technology": {
    icon: Cpu,
    color: "#3B82F6",
    stocks: ["AAPL", "MSFT", "NVDA", "AVGO", "CSCO", "ADBE", "CRM", "INTC", "AMD", "TXN", "QCOM", "AMAT", "LRCX", "ADI", "KLAC", "SNPS", "CDNS", "NXPI", "APH", "MCHP", "TEL", "FTNT", "KEYS", "ON", "MPWR", "ANSS", "SWKS", "AKAM", "GEN", "FFIV", "JNPR", "QRVO"]
  },
  "Health Care": {
    icon: Pill,
    color: "#10B981",
    stocks: ["UNH", "JNJ", "LLY", "PFE", "MRK", "ABBV", "TMO", "ABT", "DHR", "BMY", "AMGN", "GILD", "VRTX", "ISRG", "REGN", "CI", "CVS", "ELV", "HUM", "MCK", "SYK", "BDX", "ZTS", "BSX", "MDT", "EW", "DXCM", "IDXX", "A", "IQV", "MTD", "HOLX", "ALGN", "COO", "LH", "DGX", "CRL", "PKI", "RVTY", "BAX", "TECH", "BIO", "HSIC", "XRAY"]
  },
  "Financials": {
    icon: Landmark,
    color: "#F59E0B",
    stocks: ["JPM", "V", "MA", "BAC", "WFC", "GS", "MS", "BLK", "AXP", "SCHW", "C", "CB", "PGR", "MMC", "AON", "CME", "ICE", "MCO", "SPGI", "TRV", "AIG", "MET", "PRU", "ALL", "AFL", "HIG", "ACGL", "WTW", "RJF", "STT", "BK", "NTRS", "FITB", "MTB", "PNC", "USB", "CFG", "RF", "HBAN", "KEY", "CINF", "CMA", "ZION", "L", "GL", "AIZ", "BEN", "IVZ", "TROW", "AMP", "BRO"]
  },
  "Consumer Discretionary": {
    icon: ShoppingCart,
    color: "#EC4899",
    stocks: ["AMZN", "TSLA", "HD", "MCD", "NKE", "LOW", "TJX", "SBUX", "BKNG", "CMG", "MAR", "ORLY", "AZO", "ROST", "DHI", "LEN", "GM", "F", "YUM", "DPZ", "HLT", "POOL", "ULTA", "BBY", "DRI", "LVS", "WYNN", "CZR", "MGM", "CCL", "RCL", "NCLH", "TPR", "RL", "VFC", "ETSY", "EBAY", "APTV", "BWA", "LKQ", "GRMN", "GPC", "AAP"]
  },
  "Communication Services": {
    icon: Wifi,
    color: "#8B5CF6",
    stocks: ["GOOGL", "GOOG", "META", "NFLX", "DIS", "CMCSA", "VZ", "T", "TMUS", "CHTR", "EA", "TTWO", "WBD", "OMC", "IPG", "FOXA", "FOX", "PARA", "NWS", "NWSA", "MTCH", "LYV"]
  },
  "Industrials": {
    icon: Factory,
    color: "#6366F1",
    stocks: ["GE", "CAT", "BA", "HON", "UNP", "RTX", "DE", "LMT", "UPS", "NOC", "GD", "FDX", "CSX", "NSC", "WM", "EMR", "ETN", "ITW", "PH", "ROK", "CMI", "PCAR", "FAST", "ODFL", "CARR", "OTIS", "JCI", "TT", "AME", "GWW", "DOV", "IR", "CPRT", "WAB", "CTAS", "VRSK", "LII", "PWR", "XYL", "URI", "JBHT", "DAL", "UAL", "LUV", "AAL", "AXON", "TDG", "SNA", "TXT", "HII", "LDOS", "J", "BR", "EXPD", "CHRW", "AOS", "ALLE", "GNRC", "RHI", "MAS", "PNR", "NDSN", "SWK", "IEX"]
  },
  "Consumer Staples": {
    icon: Heart,
    color: "#14B8A6",
    stocks: ["PG", "KO", "PEP", "COST", "WMT", "PM", "MO", "MDLZ", "CL", "KMB", "GIS", "SYY", "KR", "STZ", "ADM", "HSY", "K", "CAG", "MKC", "CLX", "HRL", "SJM", "CPB", "CHD", "TSN", "TAP", "BG", "KDP", "EL", "MNST"]
  },
  "Energy": {
    icon: Fuel,
    color: "#EF4444",
    stocks: ["XOM", "CVX", "COP", "SLB", "EOG", "PXD", "MPC", "VLO", "PSX", "OXY", "KMI", "WMB", "HAL", "HES", "DVN", "FANG", "BKR", "TRGP", "OKE", "EQT", "APA", "MRO"]
  },
  "Utilities": {
    icon: Zap,
    color: "#A855F7",
    stocks: ["NEE", "DUK", "SO", "D", "AEP", "EXC", "SRE", "XEL", "PEG", "ED", "WEC", "ES", "AWK", "DTE", "ETR", "FE", "PPL", "AES", "CNP", "CMS", "NI", "EVRG", "ATO", "LNT", "NRG", "PNW"]
  },
  "Real Estate": {
    icon: Building2,
    color: "#0EA5E9",
    stocks: ["PLD", "AMT", "EQIX", "CCI", "PSA", "SPG", "O", "WELL", "DLR", "VICI", "AVB", "EQR", "EXR", "SBAC", "ARE", "MAA", "VTR", "INVH", "IRM", "UDR", "CPT", "KIM", "REG", "BXP", "FRT", "HST", "PEAK"]
  },
  "Materials": {
    icon: TrendingUp,
    color: "#84CC16",
    stocks: ["LIN", "APD", "SHW", "FCX", "NEM", "NUE", "ECL", "DD", "DOW", "PPG", "VMC", "MLM", "ALB", "CTVA", "CF", "MOS", "IFF", "CE", "EMN", "AVY", "IP", "PKG", "WRK", "SEE", "BALL", "AMCR", "FMC"]
  }
};

const Stocks = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState(null);

  // SEO structured data for directory page
  const stocksStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "name": "S&P 500 Stock Directory",
        "description": "Browse and research all 500+ companies in the S&P 500 index. AI-powered anomaly detection, market insights, and stock analysis.",
        "url": "https://scopethemarket.com/stocks",
        "isPartOf": {
          "@type": "WebSite",
          "name": "ScopeTheMarket",
          "url": "https://scopethemarket.com"
        },
        "mainEntity": {
          "@type": "ItemList",
          "name": "S&P 500 Companies",
          "numberOfItems": 503,
          "itemListOrder": "https://schema.org/ItemListUnordered"
        }
      },
      getBreadcrumbSchema([
        { name: "Home", url: "https://scopethemarket.com" },
        { name: "Stocks", url: "https://scopethemarket.com/stocks" }
      ])
    ]
  };

  // Flatten all stocks for search
  const allStocks = useMemo(() => {
    const stocks = [];
    Object.entries(SP500_BY_SECTOR).forEach(([sector, data]) => {
      data.stocks.forEach(symbol => {
        stocks.push({ symbol, sector });
      });
    });
    return stocks;
  }, []);

  // Filter stocks based on search and sector
  const filteredStocks = useMemo(() => {
    let result = allStocks;
    
    if (selectedSector) {
      result = result.filter(s => s.sector === selectedSector);
    }
    
    if (searchTerm) {
      const term = searchTerm.toUpperCase();
      result = result.filter(s => s.symbol.includes(term));
    }
    
    return result;
  }, [allStocks, searchTerm, selectedSector]);

  const handleStockClick = (symbol) => {
    navigate(`/app/stock/${symbol}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#080B14' }}>
      <SEOHead
        title="S&P 500 Stocks Directory | ScopeTheMarket Stock Research"
        description="Browse all 500+ S&P 500 stocks by sector. Get AI-powered market analysis, anomaly detection, institutional signals, and stock research for every company in the index."
        canonical="https://scopethemarket.com/stocks"
        ogTitle="S&P 500 Stock Directory – ScopeTheMarket"
        ogDescription="Research any S&P 500 stock. AI-driven anomaly detection, institutional trading signals, and market intelligence for all 500+ companies."
        structuredData={stocksStructuredData}
      />
      
      {/* Header */}
      <div className="py-12 px-6" style={{ backgroundColor: '#0B0F1A', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors" style={{ color: '#64748B' }}>
              Home
            </Link>
            <ChevronRight className="w-4 h-4" style={{ color: '#475569' }} />
            <span style={{ color: '#F8FAFC' }}>Stocks</span>
          </nav>
          
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
            S&P 500 Stock Research
          </h1>
          <p className="text-base mb-6" style={{ color: '#94A3B8' }}>
            Explore market research and anomaly detection for all 500+ S&P 500 companies
          </p>
          
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#64748B' }} />
            <input
              type="text"
              placeholder="Search by ticker symbol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
              style={{
                backgroundColor: '#131825',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#F8FAFC'
              }}
              data-testid="stock-search-input"
            />
          </div>
        </div>
      </div>

      {/* Sector Filter */}
      <div className="py-4 px-6 overflow-x-auto" style={{ backgroundColor: '#0A0E17' }}>
        <div className="max-w-7xl mx-auto flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedSector(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedSector ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
            style={!selectedSector ? {} : { backgroundColor: 'rgba(255,255,255,0.05)' }}
          >
            All Sectors
          </button>
          {Object.entries(SP500_BY_SECTOR).map(([sector, data]) => {
            const Icon = data.icon;
            const isSelected = selectedSector === sector;
            return (
              <button
                key={sector}
                onClick={() => setSelectedSector(isSelected ? null : sector)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isSelected ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
                style={{
                  backgroundColor: isSelected ? data.color : 'rgba(255,255,255,0.05)',
                }}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{sector}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stock Grid */}
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4 text-sm" style={{ color: '#64748B' }}>
            Showing {filteredStocks.length} stocks
          </div>
          
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2">
            {filteredStocks.map(({ symbol, sector }) => {
              const sectorData = SP500_BY_SECTOR[sector];
              return (
                <button
                  key={symbol}
                  onClick={() => handleStockClick(symbol)}
                  className="p-3 rounded-lg text-center transition-all hover:scale-105"
                  style={{
                    backgroundColor: '#131825',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                  data-testid={`stock-link-${symbol}`}
                  title={`${symbol} - ${sector}`}
                >
                  <div 
                    className="text-sm font-bold mb-1"
                    style={{ color: sectorData.color }}
                  >
                    {symbol}
                  </div>
                  <div 
                    className="w-2 h-2 rounded-full mx-auto"
                    style={{ backgroundColor: sectorData.color, opacity: 0.5 }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* SEO Content */}
      <div className="py-12 px-6" style={{ backgroundColor: '#0B0F1A', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
            About S&P 500 Stock Research
          </h2>
          <div className="space-y-4 text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
            <p>
              ScopeTheMarket provides comprehensive market research and anomaly detection for all companies 
              in the S&P 500 index. Our platform scans for unusual price movements, volume spikes, and 
              other market anomalies to help you identify stocks worth researching.
            </p>
            <p>
              Each stock page includes real-time price data, AI-generated market analysis, institutional 
              positioning summaries, political trading disclosures, and historical charts. Our anomaly 
              detection algorithms run continuously during market hours to surface potentially interesting 
              market activity.
            </p>
            <p>
              The S&P 500 is a market-capitalization-weighted index of 500 leading publicly traded companies 
              in the United States. It is widely regarded as the best gauge of large-cap U.S. equities and 
              covers approximately 80% of available market capitalization.
            </p>
          </div>
          
          {/* Sector List for SEO */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#F8FAFC' }}>
              Browse by Sector
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(SP500_BY_SECTOR).map(([sector, data]) => {
                const Icon = data.icon;
                return (
                  <div 
                    key={sector}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: '#131825', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-5 h-5" style={{ color: data.color }} />
                      <span className="font-medium" style={{ color: '#F8FAFC' }}>{sector}</span>
                    </div>
                    <p className="text-xs" style={{ color: '#64748B' }}>
                      {data.stocks.length} companies
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="py-6 px-6" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-xs leading-relaxed text-center" style={{ color: '#475569' }}>
            <strong style={{ color: '#64748B' }}>Disclaimer:</strong> ScopeTheMarket provides market research 
            and intelligence for educational and informational purposes only. Nothing on this platform constitutes 
            investment advice or a recommendation to buy or sell securities. Users are solely responsible for their 
            own investment decisions. Past performance does not guarantee future results.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Stocks;
